/* ============================================================================
   VILLAINS TCG — Product detail (cst-product.js)
   Progressive enhancement over a native product form. Everything works with JS
   off (real form, <noscript> variant <select>, native gallery swipe, <details>).
   Adds: gallery thumb/dot sync + hover-zoom + fullscreen lightbox + variant image
   switching; client variant resolution (live price / availability / SKU / URL /
   contextual disabled values); quantity stepper; AJAX add-to-cart (loading /
   success / error → cst:cart:refresh); and a sticky mobile purchase bar.
   Re-inits on shopify:section:load for the Theme Editor.
   ========================================================================== */
(() => {
  'use strict';
  const FINE = window.matchMedia('(hover: hover) and (pointer: fine)');
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  const raf = (fn) => requestAnimationFrame(fn);

  /* -------- Shopify canonical money formatter (matches Liquid `| money`) ---- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || '${{amount}}';
    const withDelims = (num, precision, thousands, decimal) => {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(num) || num == null) return '0';
      num = (num / 100.0).toFixed(precision);
      const parts = num.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cts = parts[1] ? decimal + parts[1] : '';
      return dollars + cts;
    };
    const m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!m) return fmt;
    let value = '';
    switch (m[1]) {
      case 'amount': value = withDelims(cents, 2); break;
      case 'amount_no_decimals': value = withDelims(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelims(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelims(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelims(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = withDelims(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': value = withDelims(cents, 2, ' ', ','); break;
      default: value = withDelims(cents, 2);
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  const stockLabel = (v) => {
    if (!v || !v.available) return 'Sold out';
    if (v.inv_managed && v.inv_qty <= 0 && v.inv_policy === 'continue') return 'Available on backorder';
    if (v.inv_managed && v.inv_qty > 0 && v.inv_qty <= 6) return 'Only ' + v.inv_qty + ' left';
    return 'In stock';
  };

  /* ---------------- shared: AJAX add-to-cart with button states ------------- */
  const cartAdd = async (btn, id, qty, labelSel) => {
    if (!btn || btn.classList.contains('is-loading') || btn.disabled) return;
    const label = labelSel ? (btn.querySelector(labelSel) || btn) : btn;
    const original = btn.dataset.label || label.textContent;
    btn.dataset.label = original;
    btn.classList.remove('is-success', 'is-error');
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    let ok = false;
    try {
      const routes = (typeof Theme !== 'undefined' && Theme.routes) || {};
      const res = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: qty || 1 }] }),
      });
      ok = res.ok;
    } catch (_) { ok = false; }
    btn.classList.remove('is-loading');
    btn.removeAttribute('aria-busy');
    if (ok) {
      label.textContent = 'Added ✓';
      btn.classList.add('is-success');
      document.dispatchEvent(new CustomEvent('cst:cart:refresh'));
    } else {
      label.textContent = 'Try again';
      btn.classList.add('is-error');
    }
    clearTimeout(btn._reset);
    btn._reset = setTimeout(() => {
      label.textContent = btn.dataset.label || original;
      btn.classList.remove('is-success', 'is-error');
    }, 1600);
  };

  /* ================================ GALLERY ================================ */
  const initGallery = (section) => {
    const root = section.querySelector('[data-cst-gallery]');
    if (!root) return;
    const track = root.querySelector('[data-cst-gallery-track]');
    const slides = [...root.querySelectorAll('[data-cst-slide]')];
    const thumbs = [...root.querySelectorAll('[data-cst-thumb]')];
    const dotsWrap = root.querySelector('[data-cst-gallery-dots]');
    if (!track || slides.length === 0) return;

    if (section.dataset.zoom === 'true' && FINE.matches) root.classList.add('cst-gallery--zoom');

    let index = 0;
    let scrollLock = false;

    // dots
    const dots = [];
    if (dotsWrap && slides.length > 1) {
      slides.forEach((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'cst-gallery__dot' + (i === 0 ? ' is-on' : '');
        b.setAttribute('aria-label', 'Go to image ' + (i + 1));
        b.addEventListener('click', () => go(i));
        dotsWrap.appendChild(b);
        dots.push(b);
      });
    }

    const setActive = (i) => {
      index = Math.max(0, Math.min(slides.length - 1, i));
      slides.forEach((s, x) => s.classList.toggle('is-active', x === index));
      thumbs.forEach((t, x) => {
        const on = x === index;
        t.classList.toggle('is-on', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      dots.forEach((d, x) => d.classList.toggle('is-on', x === index));
    };

    const go = (i) => {
      const n = slides.length;
      i = Math.max(0, Math.min(n - 1, i));
      scrollLock = true;
      track.scrollTo({ left: slides[i].offsetLeft - slides[0].offsetLeft, behavior: REDUCE.matches ? 'auto' : 'smooth' });
      setActive(i);
      clearTimeout(track._unlock);
      track._unlock = setTimeout(() => { scrollLock = false; }, 360);
    };

    // keep active in sync when the user swipes / scrolls the track
    let rafPending = false;
    track.addEventListener('scroll', () => {
      if (scrollLock || rafPending) return;
      rafPending = true;
      raf(() => {
        rafPending = false;
        const stride = slides.length > 1 ? (slides[1].offsetLeft - slides[0].offsetLeft) || 1 : 1;
        let i = Math.round(track.scrollLeft / stride);
        if (track.scrollLeft >= track.scrollWidth - track.clientWidth - 2) i = slides.length - 1;
        setActive(i);
      });
    }, { passive: true });

    // thumbnails
    thumbs.forEach((t, i) => t.addEventListener('click', () => go(i)));
    // roving arrow-key nav on the thumb rail (role=tablist)
    const thumbsWrap = root.querySelector('[data-cst-thumbs]');
    if (thumbsWrap) {
      thumbsWrap.addEventListener('keydown', (e) => {
        let next;
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = Math.min(thumbs.length - 1, index + 1);
        else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = Math.max(0, index - 1);
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = thumbs.length - 1;
        else return;
        e.preventDefault();
        go(next);
        thumbs[next].focus();
      });
    }

    // hover zoom on the active image
    root.querySelectorAll('.cst-gallery__img').forEach((img) => {
      img.addEventListener('mousemove', (e) => {
        if (!root.classList.contains('cst-gallery--zoom')) return;
        const r = img.getBoundingClientRect();
        img.style.setProperty('--cst-zoom-x', ((e.clientX - r.left) / r.width * 100) + '%');
        img.style.setProperty('--cst-zoom-y', ((e.clientY - r.top) / r.height * 100) + '%');
      });
      img.addEventListener('mouseleave', () => {
        img.style.setProperty('--cst-zoom-x', '50%');
        img.style.setProperty('--cst-zoom-y', '50%');
      });
    });

    /* -------- fullscreen lightbox -------- */
    const lb = section.querySelector('[data-cst-lightbox]');
    const openers = [...root.querySelectorAll('[data-cst-open-lightbox]')];
    if (lb && openers.length) {
      const lbImg = lb.querySelector('[data-cst-lb-img]');
      const shots = openers.map((b) => ({ src: b.dataset.full || b.querySelector('img')?.currentSrc || b.querySelector('img')?.src || '', alt: b.querySelector('img')?.alt || '' }));
      let cur = 0, opener = null, inerted = [];

      /* Relocate the dialog to <body> so the rest of the page can be made inert
         while it is open. Remove any stale copy this section left on a prior
         Theme-Editor re-render (matched by owner id) so nothing accumulates. */
      const owner = section.id || 'cst-pdp';
      document.querySelectorAll('body > [data-cst-lightbox][data-cst-owner="' + owner + '"]').forEach((n) => n.remove());
      lb.dataset.cstOwner = owner;
      document.body.appendChild(lb);

      const focusables = () => [...lb.querySelectorAll('button:not([disabled])')].filter((n) => n.offsetParent !== null);
      const paint = () => { if (!shots[cur]) return; lbImg.src = shots[cur].src; lbImg.alt = shots[cur].alt; };
      const open = (i) => {
        cur = i; paint();
        lb.removeAttribute('hidden');
        document.documentElement.classList.add('cst-scroll-lock');
        opener = document.activeElement;
        inerted = [];
        [...document.body.children].forEach((el) => { if (el !== lb && !el.hasAttribute('inert')) { el.setAttribute('inert', ''); inerted.push(el); } });
        (lb.querySelector('[data-cst-close]') || lb).focus();
      };
      const close = () => {
        lb.setAttribute('hidden', '');
        document.documentElement.classList.remove('cst-scroll-lock');
        inerted.forEach((el) => el.removeAttribute('inert'));
        inerted = [];
        go(cur); // keep the gallery aligned to what was viewed
        if (opener && opener.focus) opener.focus();
      };
      const step = (d) => { cur = (cur + d + shots.length) % shots.length; paint(); };

      openers.forEach((b, i) => b.addEventListener('click', (e) => { e.preventDefault(); open(i); }));
      lb.querySelector('[data-cst-close]')?.addEventListener('click', close);
      lb.querySelector('[data-cst-lb-prev]')?.addEventListener('click', () => step(-1));
      lb.querySelector('[data-cst-lb-next]')?.addEventListener('click', () => step(1));
      lb.addEventListener('click', (e) => { if (e.target === lb) close(); });
      // controller consumed by the single, page-level keydown handler (no per-section leak)
      lb._cst = { close, step, focusables, isOpen: () => !lb.hasAttribute('hidden') };
    }

    setActive(0);
    // expose a way for the variant switcher to jump to a media id
    section._galleryGoToMedia = (mediaId) => {
      if (mediaId == null) return;
      const i = slides.findIndex((s) => String(s.dataset.mediaId) === String(mediaId));
      if (i >= 0 && i !== index) go(i);
    };
  };

  /* ============================ VARIANT PICKER ============================= */
  const initVariants = (section) => {
    let variants = [];
    try { variants = JSON.parse(section.querySelector('[data-cst-pdp-variants]').textContent); } catch (_) { return; }
    if (!Array.isArray(variants) || !variants.length) return;

    const fmt = section.dataset.moneyFormat || '${{amount}}';
    const form = section.querySelector('[data-cst-product-form]');
    const idInput = section.querySelector('[data-cst-variant-id]');
    const inputs = [...section.querySelectorAll('[data-cst-option-input]')];

    const els = {
      now: section.querySelector('[data-cst-now]'),
      compare: section.querySelector('[data-cst-compare]'),
      discount: section.querySelector('[data-cst-discount]'),
      price: section.querySelector('[data-cst-price]'),
      stock: section.querySelector('[data-cst-stock]'),
      stockDot: section.querySelector('.cst-pdp__stock-dot'),
      stockLabel: section.querySelector('[data-cst-stock-label]'),
      add: section.querySelector('[data-cst-add]'),
      addLabel: section.querySelector('[data-cst-add-label]'),
      skuRow: section.querySelector('[data-cst-sku-row]'),
      sku: section.querySelector('[data-cst-sku]'),
      stickyPrice: section.querySelector('[data-cst-sticky-price]'),
      stickyAdd: section.querySelector('[data-cst-sticky-add]'),
    };

    const selected = () => {
      // read the checked value for each option position (1-based)
      const max = variants[0].options.length;
      const out = [];
      for (let pos = 1; pos <= max; pos++) {
        const c = section.querySelector(`[data-cst-option-input][data-option-position="${pos}"]:checked`);
        out.push(c ? c.dataset.value : null);
      }
      return out;
    };

    const findVariant = (opts) => variants.find((v) => v.options.every((o, i) => o === opts[i]));

    const setDiscount = (v) => {
      const onSale = v.compare_at_price && v.compare_at_price > v.price;
      if (els.compare) {
        els.compare.hidden = !onSale;
        if (onSale) els.compare.textContent = formatMoney(v.compare_at_price, fmt);
      }
      if (els.discount) {
        els.discount.hidden = !onSale;
        if (onSale) els.discount.textContent = 'Save ' + Math.round((v.compare_at_price - v.price) / v.compare_at_price * 100) + '%';
      }
      if (els.price) els.price.classList.toggle('is-sale', !!onSale);
    };

    const setAvailability = (v) => {
      const avail = !!(v && v.available);
      if (els.add) {
        els.add.disabled = !avail;
        if (els.addLabel) els.addLabel.textContent = v ? (avail ? 'Add to cart' : 'Sold out') : 'Unavailable';
      }
      if (els.stickyAdd) {
        els.stickyAdd.disabled = !avail;
        els.stickyAdd.textContent = avail ? 'Add to cart' : 'Sold out';
      }
      if (els.stockDot) els.stockDot.classList.toggle('is-oos', !avail);
      if (els.stockLabel) els.stockLabel.textContent = stockLabel(v);
    };

    const updateOptionAvailability = (opts) => {
      inputs.forEach((input) => {
        const pos = Number(input.dataset.optionPosition);
        const match = variants.some((v) => v.available
          && v.options[pos - 1] === input.dataset.value
          && opts.every((sel, i) => i === pos - 1 || sel == null || v.options[i] === sel));
        const label = input.nextElementSibling;
        if (label) label.classList.toggle('is-unavailable', !match);
      });
    };

    const update = () => {
      const opts = selected();
      // reflect chosen value labels
      opts.forEach((val, i) => {
        const chosen = section.querySelector(`[data-cst-option-chosen="${i + 1}"]`);
        if (chosen && val != null) chosen.textContent = val;
      });
      updateOptionAvailability(opts);

      const v = findVariant(opts);
      if (!v) { setAvailability(null); return; }

      if (idInput) idInput.value = v.id;
      if (els.now) els.now.textContent = formatMoney(v.price, fmt);
      if (els.stickyPrice) els.stickyPrice.textContent = formatMoney(v.price, fmt);
      setDiscount(v);
      setAvailability(v);
      if (els.skuRow) els.skuRow.hidden = !v.sku;
      if (els.sku) els.sku.textContent = v.sku || '';

      // URL
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({ variant: v.id }, '', url.toString());
      } catch (_) {}

      // gallery
      if (v.media_id != null && section._galleryGoToMedia) section._galleryGoToMedia(v.media_id);
    };

    inputs.forEach((input) => input.addEventListener('change', update));
    if (inputs.length) update(); // sync initial contextual availability

    // expose current variant id for the form/sticky add
    section._currentVariantId = () => (idInput ? idInput.value : (variants[0] && variants[0].id));
  };

  /* ============================ QUANTITY ================================== */
  const initQuantity = (section) => {
    const wrap = section.querySelector('[data-cst-qty]');
    if (!wrap) return;
    const input = wrap.querySelector('[data-cst-qty-input]');
    const dn = wrap.querySelector('[data-cst-qty-dn]');
    const up = wrap.querySelector('[data-cst-qty-up]');
    if (!input) return;
    const clamp = () => {
      let n = parseInt(input.value, 10);
      if (isNaN(n) || n < 1) n = 1;
      input.value = n;
      if (dn) dn.disabled = n <= 1;
    };
    dn && dn.addEventListener('click', () => { input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1); clamp(); });
    up && up.addEventListener('click', () => { input.value = (parseInt(input.value, 10) || 1) + 1; clamp(); });
    input.addEventListener('change', clamp);
    // prevent accidental scroll-wheel changes to the quantity while it's focused
    input.addEventListener('wheel', (e) => { if (document.activeElement === input) e.preventDefault(); }, { passive: false });
    clamp();
  };

  /* ============================ FORM + STICKY ============================= */
  const initForm = (section) => {
    const form = section.querySelector('[data-cst-product-form]');
    const idInput = section.querySelector('[data-cst-variant-id]');
    const qtyInput = section.querySelector('[data-cst-qty-input]');
    const add = section.querySelector('[data-cst-add]');
    const stickyAdd = section.querySelector('[data-cst-sticky-add]');
    const getQty = () => Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
    const getId = () => (section._currentVariantId ? section._currentVariantId() : (idInput && idInput.value));

    if (form && add) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        cartAdd(add, getId(), getQty(), '[data-cst-add-label]');
      });
    }
    if (stickyAdd) {
      stickyAdd.addEventListener('click', () => cartAdd(stickyAdd, getId(), getQty(), null));
    }

    /* sticky mobile bar: reveal once the main buy row has scrolled fully above the
       viewport. Deterministic per scroll position (rAF-throttled) so it is never
       shown at the top of the page before the shopper reaches the buy button. */
    const sticky = section.querySelector('[data-cst-pdp-sticky]');
    const anchor = section.querySelector('[data-cst-add]') || section.querySelector('.cst-pdp__buy');
    if (sticky && anchor) {
      let pending = false;
      const evaluate = () => {
        pending = false;
        const show = anchor.getBoundingClientRect().bottom < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        // keep focusability in sync with visibility (no focusable control in a hidden bar)
        sticky.toggleAttribute('inert', !show);
      };
      const onScroll = () => { if (!pending) { pending = true; raf(evaluate); } };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      evaluate();
    }
  };

  /* ========================= RECOMMENDATIONS ============================= */
  const bindRail = (sec) => {
    const row = sec.querySelector('[data-cst-rail-scroll]');
    const prev = sec.querySelector('[data-cst-rail-prev]');
    const next = sec.querySelector('[data-cst-rail-next]');
    if (!row) return;
    const step = () => {
      const c = row.querySelector('.cst-reco__cell');
      const w = c ? c.getBoundingClientRect().width + 16 : 260;
      return w * Math.max(1, Math.floor(row.clientWidth / w) - 1);
    };
    const upd = () => {
      if (prev) prev.disabled = row.scrollLeft <= 4;
      if (next) next.disabled = row.scrollLeft >= row.scrollWidth - row.clientWidth - 4;
    };
    if (prev) prev.onclick = () => row.scrollBy({ left: -step(), behavior: REDUCE.matches ? 'auto' : 'smooth' });
    if (next) next.onclick = () => row.scrollBy({ left: step(), behavior: REDUCE.matches ? 'auto' : 'smooth' });
    // bind the scroll listener once even though bindRail() runs again after the fetch swap
    if (!row.dataset.cstRailScroll) {
      row.dataset.cstRailScroll = '1';
      row.addEventListener('scroll', () => raf(upd), { passive: true });
    }
    upd();
  };

  const initReco = (sec) => {
    if (!sec || sec.dataset.cstRecoBound) return;
    sec.dataset.cstRecoBound = '1';
    bindRail(sec);
    const inner = sec.querySelector('[data-cst-reco-inner]');
    const { url, productId, sectionId } = sec.dataset;
    const intent = sec.dataset.intent || 'related';
    const limit = sec.dataset.limit || 8;
    if (!inner || !url || !productId || !sectionId) return;
    const endpoint = `${url}?section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(productId)}&intent=${encodeURIComponent(intent)}&limit=${encodeURIComponent(limit)}`;
    fetch(endpoint, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-cst-reco-inner]');
        const freshCount = fresh ? fresh.querySelectorAll('.cst-product-card').length : 0;
        const currentCount = inner.querySelectorAll('.cst-product-card').length;
        // Prefer native recommendations, but never replace a fuller fallback with a
        // sparse native result — keeps the rail complete on small catalogs.
        if (freshCount > 0 && (freshCount >= 4 || freshCount >= currentCount)) {
          inner.innerHTML = fresh.innerHTML;
          // guarantee every card in the rail loads its image immediately
          inner.querySelectorAll('img[loading="lazy"]').forEach((im) => { im.loading = 'eager'; });
          sec.hidden = false;
          bindRail(sec);
        } else if (currentCount === 0 && freshCount === 0) {
          sec.hidden = true;
        }
      })
      .catch(() => {});
  };

  /* ================================ INIT ================================== */
  const initPdp = (section) => {
    if (!section || section.dataset.cstBound) return;
    section.dataset.cstBound = '1';
    initGallery(section);
    initVariants(section);
    initQuantity(section);
    initForm(section);
  };

  const scan = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-cst-pdp]').forEach(initPdp);
    scope.querySelectorAll('[data-cst-reco]').forEach(initReco);
    if (root && root.matches) {
      if (root.matches('[data-cst-pdp]')) initPdp(root);
      if (root.matches('[data-cst-reco]')) initReco(root);
    }
  };

  /* sticky-gallery offset under the sticky header (shared scroll container) */
  const setTop = () => {
    const bar = document.querySelector('.cst-header__bar');
    const h = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--cst-pdp-top', ((h || 64) + 16) + 'px');
  };
  if (!window.__cstPdpGlobals) {
    window.__cstPdpGlobals = true;
    setTop();
    window.addEventListener('resize', setTop, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(setTop);
    document.addEventListener('shopify:section:load', (e) => { setTop(); scan(e.target || document); });

    /* One page-level keydown for whichever lightbox is open — ESC / arrows / Tab
       focus-trap. Resolves the live open dialog each keypress, so no per-section
       listener accumulates across sections or Theme-Editor re-renders. */
    document.addEventListener('keydown', (e) => {
      const lb = document.querySelector('[data-cst-lightbox]:not([hidden])');
      if (!lb || !lb._cst) return;
      if (e.key === 'Escape') { e.preventDefault(); lb._cst.close(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); lb._cst.step(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); lb._cst.step(1); }
      else if (e.key === 'Tab') {
        const f = lb._cst.focusables();
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document));
  else scan(document);
})();
